import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import {
  Building2,
  DollarSign,
  Users,
  Lightbulb,
  Globe,
  Search,
  Loader2,
  Eye,
  Edit2,
  PauseCircle,
  PlayCircle,
  Send,
  CreditCard,
  Download,
  Settings,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Mail,
  Inbox,
  Reply,
  ExternalLink,
  Filter
} from 'lucide-react';

// Stat Card Component
const StatCard = ({ title, value, change, changeType, icon: Icon, subtitle, loading }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-purple-100 card-hover">
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        {loading ? (
          <div className="h-8 flex items-center">
            <Loader2 className="animate-spin text-purple-600" size={20} />
          </div>
        ) : (
          <h3 className="text-2xl font-bold text-gray-900 truncate">{value}</h3>
        )}
        {subtitle && !loading && (
          <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
        )}
        {change && !loading && (
          <div className={`flex items-center mt-2 text-sm ${
            changeType === 'positive' ? 'text-green-600' : 'text-red-600'
          }`}>
            {changeType === 'positive' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span className="ml-1">{change}</span>
          </div>
        )}
      </div>
      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 ml-4">
        <Icon className="text-purple-600" size={24} />
      </div>
    </div>
  </div>
);

// Quick Action Button
const QuickAction = ({ title, description, icon: Icon, onClick, variant = 'default' }) => {
  const bgColors = {
    default: 'bg-fuchsia-100',
    success: 'bg-green-100',
    warning: 'bg-yellow-100',
    danger: 'bg-red-100',
    info: 'bg-blue-100'
  };
  
  const textColors = {
    default: 'text-fuchsia-600',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    danger: 'text-red-600',
    info: 'text-blue-600'
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl p-4 shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-left"
    >
      <div className="flex items-start space-x-4">
        <div className={`w-10 h-10 ${bgColors[variant]} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={textColors[variant]} size={20} />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900">{title}</h4>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
    trial: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-gray-100 text-gray-800'
  };
  
  return (
    <span className={`badge ${styles[status] || styles.pending}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

// Plan Badge Component
const PlanBadge = ({ plan }) => {
  const styles = {
    starter: 'bg-gray-100 text-gray-800',
    growth: 'bg-purple-100 text-purple-800',
    scale: 'bg-fuchsia-100 text-fuchsia-800',
    enterprise: 'bg-gradient-to-r from-purple-100 to-fuchsia-100 text-purple-800'
  };
  
  return (
    <span className={`badge ${styles[plan?.toLowerCase()] || styles.starter}`}>
      {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
    </span>
  );
};

// Feature Request Status Badge
const FeatureStatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800',
    under_review: 'bg-blue-100 text-blue-800',
    planned: 'bg-purple-100 text-purple-800',
    declined: 'bg-red-100 text-red-800',
    completed: 'bg-green-100 text-green-800'
  };
  
  const labels = {
    pending: 'Pending',
    under_review: 'Under Review',
    planned: 'Planned',
    declined: 'Declined',
    completed: 'Completed'
  };
  
  return (
    <span className={`badge ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const SuperAdminDashboard = () => {
  useAuthStore();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalRevenue: 0,
    totalUsers: 0,
    featureRequests: 0,
    pendingDomains: 0,
    supportTickets: 0,
    complaints: 0,
    churnRate: 0
  });
  const [tenants, setTenants] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);
  const [featureRequests, setFeatureRequests] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [waitlistSignups, setWaitlistSignups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcement, setAnnouncement] = useState({ title: '', message: '' });
  const [activeTab, setActiveTab] = useState('tenants');
  
  // Contact tab state
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  const [contactTypeFilter, setContactTypeFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchTenants(),
        fetchRecentSignups(),
        fetchFeatureRequests(),
        fetchContactSubmissions(),
        fetchWaitlistSignups()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    // Fetch active tenants count
    const { count: tenantsCount } = await supabase
      .from('tenants')
      .select('*', { count: 'exact', head: true })
      .neq('status', 'cancelled');

    // Fetch total revenue from payments
    const { data: payments } = await supabase
      .from('payments')
      .select('amount')
      .eq('status', 'completed');

    const totalRevenue = payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

    // Fetch total users (parents + employees)
    const { count: usersCount } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .in('role', ['parent', 'employee']);

    // Fetch feature requests
    const { count: featuresCount } = await supabase
      .from('feature_requests')
      .select('*', { count: 'exact', head: true });

    // Fetch pending custom domain requests
    const { data: pendingDomainsData } = await supabase
      .from('tenants')
      .select('settings')
      .not('settings->custom_domain_request', 'is', null);

    const pendingDomains = pendingDomainsData?.filter(
      t => t.settings?.custom_domain_request?.status === 'pending'
    ).length || 0;

    // Fetch new contact form submissions
    const { count: contactCount } = await supabase
      .from('contact_form_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'new');
    
    // Fetch waitlist signups
    const { count: waitlistCount } = await supabase
      .from('waitlist_signups')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');
    
    const supportTickets = (contactCount || 0) + (waitlistCount || 0);
    
    // Fetch complaints (placeholder - would need complaints table)
    const complaints = 0; // TODO: Implement complaints table
    
    // Calculate churn rate (placeholder)
    const churnRate = 0; // TODO: Calculate from tenant history

    setStats({
      totalTenants: tenantsCount || 0,
      totalRevenue,
      totalUsers: usersCount || 0,
      featureRequests: featuresCount || 0,
      pendingDomains,
      supportTickets,
      complaints,
      churnRate
    });
  };

  // Fetch all dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTenants = async () => {
    const { data, error } = await supabase
      .from('tenants')
      .select('*, users(count)')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tenants:', error);
      return;
    }

    setTenants(data || []);
  };

  const fetchRecentSignups = async () => {
    const { data, error } = await supabase
      .from('tenants')
      .select('id, name, email, plan, status, created_at')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching recent signups:', error);
      return;
    }

    setRecentSignups(data || []);
  };

  const fetchFeatureRequests = async () => {
    const { data, error } = await supabase
      .from('feature_requests')
      .select('*, tenants(name)')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching feature requests:', error);
      return;
    }

    setFeatureRequests(data || []);
  };

  const fetchContactSubmissions = async () => {
    const { data, error } = await supabase
      .from('contact_form_submissions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching contact submissions:', error);
      return;
    }

    setContactSubmissions(data || []);
  };

  const fetchWaitlistSignups = async () => {
    const { data, error } = await supabase
      .from('waitlist_signups')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching waitlist signups:', error);
      return;
    }

    setWaitlistSignups(data || []);
  };

  // Contact submissions handlers
  const handleUpdateSubmissionStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('contact_form_submissions')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setContactSubmissions(contactSubmissions.map(s => 
        s.id === id ? { ...s, status: newStatus } : s
      ));

      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating submission status:', error);
      alert('Failed to update status');
    }
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const { error } = await supabase
        .from('contact_form_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setContactSubmissions(contactSubmissions.filter(s => s.id !== id));
      setShowSubmissionModal(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission');
    }
  };

  const openSubmissionModal = (submission) => {
    setSelectedSubmission(submission);
    setShowSubmissionModal(true);
    if (submission.status === 'new') {
      handleUpdateSubmissionStatus(submission.id, 'read');
    }
  };

  const handleSuspendTenant = async (tenantId, currentStatus) => {
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    try {
      const { error } = await supabase
        .from('tenants')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', tenantId);

      if (error) throw error;

      // Update local state
      setTenants(tenants.map(t => 
        t.id === tenantId ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      console.error('Error updating tenant status:', error);
      alert('Failed to update tenant status');
    }
  };

  const handleUpdateFeatureStatus = async (requestId, newStatus) => {
    try {
      const { error } = await supabase
        .from('feature_requests')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      setFeatureRequests(featureRequests.map(fr => 
        fr.id === requestId ? { ...fr, status: newStatus } : fr
      ));
    } catch (error) {
      console.error('Error updating feature status:', error);
      alert('Failed to update feature request status');
    }
  };

  const handleSendAnnouncement = async () => {
    try {
      // In a real implementation, this would send to all tenants or selected ones
      alert(`Announcement "${announcement.title}" would be sent to all tenants`);
      setShowAnnouncementModal(false);
      setAnnouncement({ title: '', message: '' });
    } catch (error) {
      console.error('Error sending announcement:', error);
      alert('Failed to send announcement');
    }
  };

  const handleExportTenants = () => {
    const csv = [
      ['Name', 'Subdomain', 'Plan', 'Status', 'Created Date', 'Custom Domain'].join(','),
      ...tenants.map(t => [
        t.name,
        t.subdomain,
        t.plan,
        t.status,
        new Date(t.created_at).toLocaleDateString(),
        t.settings?.custom_domain || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenants-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };


  // Filter tenants based on search and status
  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Contact tab helpers
  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const filteredContactSubmissions = contactSubmissions.filter(submission => {
    const matchesSearch = 
      submission.first_name?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
      submission.last_name?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
      submission.email?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
      submission.school?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
      submission.message?.toLowerCase().includes(contactSearchTerm.toLowerCase());
    
    const matchesStatus = contactStatusFilter === 'all' || submission.status === contactStatusFilter;
    const matchesType = contactTypeFilter === 'all' || submission.inquiry_type === contactTypeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const exportContactSubmissions = () => {
    const csv = [
      ['Date', 'Name', 'Email', 'School', 'Type', 'Status', 'Message'].join(','),
      ...filteredContactSubmissions.map(s => [
        new Date(s.created_at).toISOString(),
        `"${s.first_name} ${s.last_name}"`,
        s.email,
        `"${s.school || ''}"`,
        s.inquiry_type,
        s.status,
        `"${(s.message || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Super Admin Dashboard
            </h1>
            <p className="text-gray-500 mt-1">
              Platform overview for support@culturalbility.com
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-2">
            <span className="badge badge-info">
              <Globe size={12} className="mr-1" />
              Platform Admin
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid - SaaS Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Active Tenants"
          value={stats.totalTenants}
          change="+12 this month"
          changeType="positive"
          icon={Building2}
          loading={loading}
        />
        <StatCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(stats.totalRevenue / 12)}
          subtitle={`ARR: ${formatCurrency(stats.totalRevenue)}`}
          change="+8.5% vs last month"
          changeType="positive"
          icon={DollarSign}
          loading={loading}
        />
        <StatCard
          title="Support Tickets"
          value={stats.supportTickets}
          subtitle="Open tickets"
          change="0 critical"
          changeType="positive"
          icon={Mail}
          loading={loading}
        />
        <StatCard
          title="Churn Rate"
          value={`${stats.churnRate}%`}
          subtitle="Monthly churn"
          change="-0.5% vs last month"
          changeType="positive"
          icon={TrendingDown}
          loading={loading}
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            title="Send Announcement"
            description="Send platform-wide notification to all tenants"
            icon={Send}
            onClick={() => setShowAnnouncementModal(true)}
            variant="info"
          />
          <QuickAction
            title="View All Payments"
            description="Review payment history across all tenants"
            icon={CreditCard}
            onClick={() => alert('Navigate to payments view')}
            variant="default"
          />
          <QuickAction
            title="Export Tenant List"
            description="Download CSV of all tenant data"
            icon={Download}
            onClick={handleExportTenants}
            variant="success"
          />
          <QuickAction
            title="System Settings"
            description="Manage global platform configuration"
            icon={Settings}
            onClick={() => alert('Navigate to system settings')}
            variant="warning"
          />
        </div>
        
        {/* Analytics & Engagement */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <QuickAction
            title="Contact Submissions"
            description={`View and manage ${contactSubmissions.filter(s => s.status === 'new').length} new contact form submissions`}
            icon={Inbox}
            onClick={() => setActiveTab('contact')}
            variant="info"
          />
          <QuickAction
            title="Waitlist Signups"
            description={`${waitlistSignups.filter(s => s.status === 'pending').length} pending waitlist signups`}
            icon={Users}
            onClick={() => setActiveTab('waitlist')}
            variant="success"
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-purple-200">
        <div className="flex space-x-6 overflow-x-auto pb-2">
          {[
            { id: 'tenants', label: 'All Tenants', count: tenants.length },
            { id: 'signups', label: 'Recent Signups', count: recentSignups.length },
            { id: 'features', label: 'Feature Requests', count: featureRequests.length },
            { id: 'waitlist', label: 'Waitlist', count: waitlistSignups.length },
            { id: 'contact', label: 'Contact Forms', count: contactSubmissions.filter(s => s.status === 'new').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Tenants Tab */}
      {activeTab === 'tenants' && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-purple-100 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search tenants by name, subdomain, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-12"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter size={18} className="text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input py-2"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="trial">Trial</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Tenants Table */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-purple-600" size={32} />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[600px]">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">School Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Subdomain</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Custom Domain</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-purple-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-50">
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No tenants found matching your criteria
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-purple-50/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <span className="text-purple-700 font-semibold">
                                {tenant.name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{tenant.name}</p>
                              <p className="text-sm text-gray-500">{tenant.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                            {tenant.subdomain}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          <PlanBadge plan={tenant.plan} />
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={tenant.status} />
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {formatDate(tenant.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          {tenant.settings?.custom_domain ? (
                            <div className="flex items-center space-x-2">
                              <Globe size={14} className="text-purple-500" />
                              <span className="text-sm text-gray-700">{tenant.settings.custom_domain}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowTenantModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit Tenant"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleSuspendTenant(tenant.id, tenant.status)}
                              className={`p-2 rounded-lg transition-colors ${
                                tenant.status === 'suspended'
                                  ? 'text-green-400 hover:text-green-600 hover:bg-green-50'
                                  : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                              }`}
                              title={tenant.status === 'suspended' ? 'Activate' : 'Suspend'}
                            >
                              {tenant.status === 'suspended' ? <PlayCircle size={18} /> : <PauseCircle size={18} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Recent Signups Tab */}
      {activeTab === 'signups' && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-purple-600" size={32} />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[600px]">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">School Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Signup Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-purple-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-50">
                  {recentSignups.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        No recent signups
                      </td>
                    </tr>
                  ) : (
                    recentSignups.map((signup) => (
                      <tr key={signup.id} className="hover:bg-purple-50/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-fuchsia-100 rounded-lg flex items-center justify-center">
                              <span className="text-fuchsia-700 font-semibold">
                                {signup.name?.[0]}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900">{signup.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-500">{signup.email}</td>
                        <td className="px-6 py-4">
                          <PlanBadge plan={signup.plan} />
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={signup.status} />
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          <div className="flex items-center space-x-2">
                            <Calendar size={14} className="text-gray-400" />
                            <span>{formatDate(signup.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => {
                              setSelectedTenant(signup);
                              setShowTenantModal(true);
                            }}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Feature Requests Tab */}
      {activeTab === 'features' && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-purple-600" size={32} />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[600px]">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">School Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Request Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Requested Domain</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-purple-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-50">
                  {featureRequests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No feature requests yet
                      </td>
                    </tr>
                  ) : (
                    featureRequests.map((request) => (
                      <tr key={request.id} className="hover:bg-purple-50/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Lightbulb size={18} className="text-purple-600" />
                            </div>
                            <span className="font-medium text-gray-900">{request.tenants?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="badge badge-info">
                            {request.type?.charAt(0).toUpperCase() + request.type?.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 max-w-xs truncate">
                          {request.description}
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {request.requested_domain ? (
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                              {request.requested_domain}
                            </code>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {formatDate(request.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <FeatureStatusBadge status={request.status} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {request.status !== 'under_review' && (
                              <button
                                onClick={() => handleUpdateFeatureStatus(request.id, 'under_review')}
                                className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Mark as Under Review"
                              >
                                <Clock size={18} />
                              </button>
                            )}
                            {request.status !== 'planned' && (
                              <button
                                onClick={() => handleUpdateFeatureStatus(request.id, 'planned')}
                                className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Mark as Planned"
                              >
                                <CheckCircle size={18} />
                              </button>
                            )}
                            {request.status !== 'declined' && (
                              <button
                                onClick={() => handleUpdateFeatureStatus(request.id, 'declined')}
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Decline Request"
                              >
                                <XCircle size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Waitlist Tab */}
      {activeTab === 'waitlist' && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-purple-600" size={32} />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full min-w-[600px]">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">School</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-50">
                  {waitlistSignups.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No waitlist signups yet
                      </td>
                    </tr>
                  ) : (
                    waitlistSignups.map((signup) => (
                      <tr key={signup.id} className="hover:bg-purple-50/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <span className="text-green-700 font-semibold">
                                {signup.first_name?.[0]}{signup.last_name?.[0]}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900">
                              {signup.first_name} {signup.last_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <a href={`mailto:${signup.email}`} className="text-purple-600 hover:text-purple-700">
                            {signup.email}
                          </a>
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {signup.school || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {signup.role && (
                            <span className="badge bg-gray-100 text-gray-700 capitalize">
                              {signup.role}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {signup.source}
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {formatDate(signup.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`badge ${
                            signup.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            signup.status === 'notified' ? 'bg-blue-100 text-blue-800' :
                            signup.status === 'converted' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {signup.status?.charAt(0).toUpperCase() + signup.status?.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Contact Forms Tab */}
      {activeTab === 'contact' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by name, email, school, or message..."
                  value={contactSearchTerm}
                  onChange={(e) => setContactSearchTerm(e.target.value)}
                  className="form-input pl-12 w-full"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Filter size={18} className="text-gray-400" />
                <select
                  value={contactStatusFilter}
                  onChange={(e) => setContactStatusFilter(e.target.value)}
                  className="form-input py-2"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="spam">Spam</option>
                </select>
                
                <select
                  value={contactTypeFilter}
                  onChange={(e) => setContactTypeFilter(e.target.value)}
                  className="form-input py-2"
                >
                  <option value="all">All Types</option>
                  <option value="sales">Sales</option>
                  <option value="support">Support</option>
                  <option value="demo">Demo</option>
                  <option value="partnership">Partnership</option>
                  <option value="other">Other</option>
                </select>
                
                <button
                  onClick={exportContactSubmissions}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                >
                  <ExternalLink size={18} />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>

          {/* Submissions Table */}
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-purple-600" size={32} />
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Message</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-purple-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-50">
                    {filteredContactSubmissions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          {contactSearchTerm || contactStatusFilter !== 'all' || contactTypeFilter !== 'all'
                            ? 'No submissions match your filters'
                            : 'No contact form submissions yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredContactSubmissions.map((submission) => (
                        <tr 
                          key={submission.id} 
                          className={`hover:bg-purple-50/50 cursor-pointer ${submission.status === 'new' ? 'bg-blue-50/30' : ''}`}
                          onClick={() => openSubmissionModal(submission)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                submission.status === 'new' ? 'bg-blue-100' : 'bg-purple-100'
                              }`}>
                                <span className={`font-semibold ${
                                  submission.status === 'new' ? 'text-blue-700' : 'text-purple-700'
                                }`}>
                                  {submission.first_name?.[0]}{submission.last_name?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {submission.first_name} {submission.last_name}
                                </p>
                                {submission.school && (
                                  <p className="text-sm text-gray-500">{submission.school}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <a 
                              href={`mailto:${submission.email}`}
                              className="text-sm text-purple-600 hover:text-purple-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {submission.email}
                            </a>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`badge ${
                              submission.inquiry_type === 'sales' ? 'bg-purple-100 text-purple-800' :
                              submission.inquiry_type === 'support' ? 'bg-blue-100 text-blue-800' :
                              submission.inquiry_type === 'demo' ? 'bg-green-100 text-green-800' :
                              submission.inquiry_type === 'partnership' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {submission.inquiry_type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600 max-w-xs truncate">
                              {submission.message}
                            </p>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatRelativeDate(submission.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`badge ${
                              submission.status === 'new' ? 'bg-blue-100 text-blue-800' :
                              submission.status === 'read' ? 'bg-gray-100 text-gray-800' :
                              submission.status === 'replied' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {submission.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSubmissionModal(submission);
                                }}
                                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                                title="View"
                              >
                                <Eye size={18} />
                              </button>
                              {submission.status !== 'replied' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateSubmissionStatus(submission.id, 'replied');
                                  }}
                                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                                  title="Mark Replied"
                                >
                                  <Reply size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submission Detail Modal */}
      {showSubmissionModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                  <span className="text-purple-700 font-bold text-xl">
                    {selectedSubmission.first_name?.[0]}{selectedSubmission.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedSubmission.first_name} {selectedSubmission.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">{selectedSubmission.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowSubmissionModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Type</p>
                  <span className={`badge ${
                    selectedSubmission.inquiry_type === 'sales' ? 'bg-purple-100 text-purple-800' :
                    selectedSubmission.inquiry_type === 'support' ? 'bg-blue-100 text-blue-800' :
                    selectedSubmission.inquiry_type === 'demo' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedSubmission.inquiry_type}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <span className={`badge ${
                    selectedSubmission.status === 'new' ? 'bg-blue-100 text-blue-800' :
                    selectedSubmission.status === 'replied' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedSubmission.status}
                  </span>
                </div>
                {selectedSubmission.school && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">School</p>
                    <p className="text-gray-900 font-medium">{selectedSubmission.school}</p>
                  </div>
                )}
                {selectedSubmission.newsletter && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Newsletter</p>
                    <p className="text-green-700 font-medium">Subscribed ✓</p>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Message</h3>
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                  {selectedSubmission.message}
                </div>
              </div>

              <div className="text-xs text-gray-400">
                <p>Submitted: {new Date(selectedSubmission.created_at).toLocaleString()}</p>
                <p>IP: {selectedSubmission.ip_address || 'Unknown'}</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between">
              <div className="flex space-x-2">
                {selectedSubmission.status !== 'replied' && (
                  <button
                    onClick={() => handleUpdateSubmissionStatus(selectedSubmission.id, 'replied')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                  >
                    <CheckCircle size={18} />
                    <span>Mark Replied</span>
                  </button>
                )}
              </div>
              
              <div className="flex space-x-2">
                <a
                  href={`mailto:${selectedSubmission.email}?subject=Re: Your ${selectedSubmission.inquiry_type} inquiry`}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
                >
                  <Mail size={18} />
                  <span>Reply</span>
                </a>
                <button
                  onClick={() => handleDeleteSubmission(selectedSubmission.id)}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Detail Modal */}
      {showTenantModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                  <span className="text-purple-700 font-bold text-2xl">
                    {selectedTenant.name?.[0]}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedTenant.name}</h2>
                  <p className="text-gray-500">{selectedTenant.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowTenantModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Subdomain</p>
                <code className="text-gray-900 font-medium">{selectedTenant.subdomain}</code>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Plan</p>
                <PlanBadge plan={selectedTenant.plan} />
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <StatusBadge status={selectedTenant.status} />
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Created</p>
                <p className="text-gray-900 font-medium">{formatDate(selectedTenant.created_at)}</p>
              </div>
            </div>

            {selectedTenant.settings?.custom_domain && (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Globe size={18} className="text-purple-600" />
                  <span className="font-medium text-purple-900">Custom Domain</span>
                </div>
                <code className="text-purple-700">{selectedTenant.settings.custom_domain}</code>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setShowTenantModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleSuspendTenant(selectedTenant.id, selectedTenant.status);
                  setShowTenantModal(false);
                }}
                className={`flex-1 px-4 py-2 rounded-lg text-white ${
                  selectedTenant.status === 'suspended'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {selectedTenant.status === 'suspended' ? 'Activate Tenant' : 'Suspend Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Send Platform Announcement</h2>
            <div className="space-y-4">
              <div>
                <label className="form-label">Announcement Title</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter announcement title..."
                  value={announcement.title}
                  onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })}
                />
              </div>
              <div>
                <label className="form-label">Message</label>
                <textarea
                  className="form-input min-h-[120px]"
                  placeholder="Enter your message..."
                  value={announcement.message}
                  onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })}
                />
              </div>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowAnnouncementModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendAnnouncement}
                disabled={!announcement.title || !announcement.message}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Announcement
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SuperAdminDashboard;
